import { useData } from "../runtime/provider";
import LInput from "@components/LInput";
import LScreen from "@components/LScreen";
import LScrollView from "@components/LScrollView";
import LText from "@components/LText";
import LButton from "@components/LButton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { router, useLocalSearchParams } from "expo-router";
import { captureException } from "@utils/captureException";
import LBox from "@components/LBox";
import { useAnimatedStyle, withTiming } from "react-native-reanimated";
import LIcon from "@components/LIcon";
import { fonts, hexToRGB, theme } from "@theme";
import { Alert, Dimensions, Platform, TextInput } from "react-native";
import Eye from "@components/Eye";
import { auth } from "@utils/firebase";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import useLoginRegister from "@hooks/useLoginRegister";
import { useKeyboardOpen } from "@hooks/useKeyboardOpen";
import { isSafari } from "@utils/isSafari";
import {
  initialWindowMetrics,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

import { toast } from "sonner-native";

const redirectOnComplete = async () => {
  // Native has no browser `location`; the user is already in the app, so the
  // universal link would be a no-op there anyway — just go home.
  if (Platform.OS === "web") {
    window.location.href = "https://open.lissen.com/";
  } else {
    router.replace("/");
  }
};

const alert = (title: string | null, message?: string) => {
  if (Platform.OS === "web") {
    window.alert(message);
  } else {
    if (title) {
      Alert.alert(title, message);
    } else {
      Alert.alert(message ?? "");
    }
  }
};

export default function SetPasswordScreen() {
  const session = useData();
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [emailInput, setEmailInput] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingResetEmail, setIsSendingResetEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { mode, oobCode } = useLocalSearchParams<{
    mode: string;
    oobCode: string;
  }>();
  const passwordRef = useRef<TextInput>(null);
  const emailRef = useRef<TextInput>(null);
  const showPasswordButtonDisabled = password.length === 0 || isLoading;
  const validPassword = password.length >= 6;
  const { authenticate, isSubmitting } = useLoginRegister(
    undefined,
    redirectOnComplete,
  );
  // The two synchronous validation failures (wrong mode / missing oobCode) are
  // pure derivations of the route params, so seed the state from them at mount
  // and re-apply them during render when the params change, rather than in the
  // verification effect below. The async verification result (handled in the
  // effect) overrides these afterwards for the valid path.
  const verificationParamsInvalid = mode !== "resetPassword" || !oobCode;
  const INVALID_CODE_MESSAGE =
    "Unable to verify password reset code. Please request a new one.";

  const [invalidCode, setInvalidCode] = useState<boolean>(
    verificationParamsInvalid,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(
    verificationParamsInvalid ? INVALID_CODE_MESSAGE : null,
  );

  const [prevVerificationParams, setPrevVerificationParams] = useState<string>(
    `${mode}|${oobCode}`,
  );
  if (prevVerificationParams !== `${mode}|${oobCode}`) {
    setPrevVerificationParams(`${mode}|${oobCode}`);
    if (verificationParamsInvalid) {
      setInvalidCode(true);
      setErrorMessage(INVALID_CODE_MESSAGE);
    }
  }
  const continueButtonEnabled =
    validPassword && email !== undefined && !isLoading && !isSubmitting;

  const title = "Set Password";
  const buttonLabel = "SET PASSWORD";

  const screenStyle = useAnimatedStyle(() => {
    return {
      opacity: withTiming(isLoading ? 0.3 : 1, { duration: 350 }),
    };
  });

  useEffect(() => {
    if (mode !== "resetPassword") {
      // Invalid state already derived during render; nothing async to do.
      return;
    }

    if (!oobCode) {
      // Invalid state already derived during render; surface the alert only.
      alert("Error", "OOB code is missing");
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((result) => {
        setEmail(result);
        setInvalidCode(false);
        setErrorMessage(null);
      })
      .catch((error) => {
        console.error("Password reset code verification error:", error);
        setInvalidCode(true);

        // Determine specific error message
        if (
          error.code === "auth/invalid-action-code" ||
          error.code === "auth/expired-action-code"
        ) {
          setErrorMessage(
            "This password reset link is invalid or has expired. Please request a new one.",
          );
        } else {
          setErrorMessage(
            "Unable to verify password reset code. Please request a new one.",
          );
        }
      });
  }, [mode, oobCode]);

  const resetPassword = useCallback(async () => {
    setIsLoading(true);
    try {
      if (mode !== "resetPassword") {
        alert("Error", "Something went wrong, please try again later.");
        return;
      }

      if (!oobCode) {
        alert("Error", "OOB code is missing");
        return;
      }

      await confirmPasswordReset(auth, oobCode, password);

      if (email) {
        try {
          await authenticate(undefined, undefined, email, password);
        } catch {
          router.replace("/login");
        }
      } else {
        alert("Password set successfully");
        router.replace("/login");
      }
    } catch (error) {
      console.error(`::ResetPasswordScreen - resetPassword error`, error);
      alert(null, "Error resetting password, please try again later.");
      captureException(error, {
        channel: "error",
        area: "auth",
        operation: "reset-password",
      });
    } finally {
      setIsLoading(false);
    }
  }, [password, mode, oobCode]);

  useEffect(() => {
    if (!invalidCode) {
      passwordRef.current?.focus();
    } else {
      emailRef.current?.focus();
    }
  }, [passwordRef, emailRef, invalidCode]);

  const handleRequestNewCode = useCallback(async () => {
    if (!emailInput.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setIsSendingResetEmail(true);
    try {
      await session.api.requestPasswordReset.mutate({
        email: emailInput.trim(),
      });
      toast.success(
        `We've sent a new password reset link to ${emailInput.trim()}`,
      );
      // Optionally redirect to login or show success message
      setTimeout(() => {
        router.replace("/login");
      }, 2000);
    } catch (error: any) {
      console.error("Error sending password reset email:", error);
      // The API always returns success to prevent email enumeration,
      // but we still show a generic error if the request fails
      toast.error("Failed to send password reset email. Please try again.");
    } finally {
      setIsSendingResetEmail(false);
    }
  }, [emailInput]);

  const keyboardOpen = useKeyboardOpen();
  const isIOSSafari = isSafari();
  const insets = useSafeAreaInsets();
  const bottomPadding = useMemo(() => {
    if (Platform.OS === "web") {
      if (keyboardOpen) {
        return Dimensions.get("window").height * (isIOSSafari ? 0.2 : 0.3);
      }
    }

    if (Platform.OS === "android") {
      return initialWindowMetrics?.insets?.bottom ?? 20;
    }

    return insets.bottom + theme.spacing.s3;
  }, [keyboardOpen, isIOSSafari, insets]);

  return (
    <LScreen keyboard>
      <LScrollView
        contentContainerStyle={{ flexGrow: 1, paddingBottom: bottomPadding }}
        keyboardDismissMode={"on-drag"}
        disableAutomaticPadding
      >
        <LBox
          flex={1}
          justifyContent="center"
          alignItems="center"
          style={screenStyle}
        >
          <LBox width="100%" alignItems="center">
            <LBox alignItems="center">
              <LIcon name="logo_round_large" size={80} />
            </LBox>
            <LBox mb="m2" mt="l2" alignItems="center">
              {/* Title */}
              <LText
                fontSize={28}
                lineHeight={33.6}
                fontFamily={fonts.semiBold}
              >
                {invalidCode ? "Invalid Link" : title}
              </LText>
              {/* Error message or email subtitle */}
              {invalidCode && errorMessage ? (
                <LBox mt="s2" px="m1" alignItems="center">
                  <LText
                    fontSize={16}
                    lineHeight={20}
                    fontFamily={fonts.regular}
                    color="error"
                    textAlign="center"
                  >
                    {errorMessage}
                  </LText>
                </LBox>
              ) : email ? (
                <LText
                  fontSize={16}
                  lineHeight={20}
                  fontFamily={fonts.regular}
                  color="greyText"
                  mt="s2"
                >
                  Set your password for {email}
                </LText>
              ) : null}
            </LBox>
            <LBox width="100%" gap="s3">
              {invalidCode ? (
                // Show email input for requesting new code
                <LBox justifyContent="center">
                  <LInput
                    placeholder="Enter your email"
                    ref={emailRef}
                    variant="auth"
                    value={emailInput}
                    onChangeText={setEmailInput}
                    textContentType="emailAddress"
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="email"
                    keyboardType="email-address"
                    returnKeyType="send"
                    enablesReturnKeyAutomatically
                    placeholderTextColor={hexToRGB(theme.colors.white, 0.3)}
                    editable={!isSendingResetEmail}
                    selectTextOnFocus={!isSendingResetEmail}
                    style={{
                      opacity: isSendingResetEmail ? 0.5 : 1,
                    }}
                    bottomSheet={false}
                    onSubmitEditing={handleRequestNewCode}
                  />
                </LBox>
              ) : (
                // Show password input for setting password
                <LBox justifyContent="center">
                  <LInput
                    placeholder="Password"
                    ref={passwordRef}
                    variant="auth"
                    value={password}
                    onChangeText={setPassword}
                    textContentType="oneTimeCode"
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="password"
                    secureTextEntry={!showPassword}
                    onSubmitEditing={resetPassword}
                    returnKeyType="go"
                    enablesReturnKeyAutomatically
                    placeholderTextColor={hexToRGB(theme.colors.white, 0.3)}
                    editable={!isLoading}
                    selectTextOnFocus={!isLoading}
                    style={{
                      opacity: isLoading ? 0.5 : 1,
                    }}
                    bottomSheet={false}
                  />
                  <Eye
                    disabled={showPasswordButtonDisabled}
                    showPassword={showPassword}
                    onPress={() => setShowPassword((prev) => !prev)}
                  />
                </LBox>
              )}
            </LBox>
          </LBox>
        </LBox>
        <LBox width="100%" justifyContent="flex-end" gap="m1" pb="m1">
          {invalidCode ? (
            <>
              <LButton
                label={
                  isSendingResetEmail ? "SENDING..." : "GET NEW RESET LINK"
                }
                variant={
                  emailInput.trim().length > 0 && !isSendingResetEmail
                    ? "authActive"
                    : "auth"
                }
                onPress={handleRequestNewCode}
                enabled={emailInput.trim().length > 0 && !isSendingResetEmail}
                testID="RequestNewCodeButton"
                sentryEventName="request_new_password_reset"
              />
              <LButton
                label="BACK TO LOGIN"
                variant="roundedWhite"
                onPress={() => router.replace("/login")}
                sentryEventName="reset_password_back_to_login"
              />
            </>
          ) : (
            <>
              <LButton
                label={buttonLabel}
                variant={continueButtonEnabled ? "authActive" : "auth"}
                onPress={resetPassword}
                enabled={continueButtonEnabled}
                testID="SignUpButton"
                sentryEventName="login_continue"
              />
              <LButton
                label="CANCEL"
                variant="roundedWhite"
                onPress={() => router.replace("/")}
                sentryEventName="reset_password_back_to_app"
              />
            </>
          )}
        </LBox>
      </LScrollView>
    </LScreen>
  );
}
